import { usePWA } from "@/hooks/use-pwa";

/**
 * PWA 설치 안내 화면
 * 일반 브라우저에서 접속 시 전체 화면으로 표시
 */
export function InstallPrompt() {
  const { isIOS, iosVersion, canInstall, promptInstall } = usePWA();

  const handleInstallClick = async () => {
    const installed = await promptInstall();
    if (installed) {
      // 설치 완료 후 페이지 새로고침
      window.location.reload();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white px-6">
      {/* 앱 아이콘 */}
      <div className="mb-6">
        <img
          src="/icons/icon-192x192.png"
          alt="그로잉어학원"
          width={120}
          height={120}
          className="rounded-3xl shadow-lg"
          loading="lazy"
        />
      </div>

      {/* 타이틀 */}
      <h1 className="mb-2 text-2xl font-bold text-gray-900">그로잉어학원</h1>
      <p className="mb-8 text-center text-gray-600">
        앱을 설치하여 더 편리하게 이용하세요
      </p>

      {/* 설치 안내 */}
      {isIOS ? (
        <IOSInstallGuide iosVersion={iosVersion} />
      ) : canInstall ? (
        <AndroidInstallButton onInstall={handleInstallClick} />
      ) : (
        <AndroidInstallGuide />
      )}
    </div>
  );
}

/**
 * iOS 설치 안내
 */
function IOSInstallGuide({ iosVersion }: { iosVersion: number | null }) {
  // iOS 18 이상에서는 더보기 버튼이 추가됨
  const isNewSafari = iosVersion !== null && iosVersion >= 18;

  return (
    <div className="w-full max-w-sm rounded-2xl bg-gray-50 p-6">
      <p className="mb-4 text-center text-sm font-medium text-gray-700">
        Safari에서 아래 단계를 따라 설치해주세요
      </p>

      <ol className="space-y-4">
        {isNewSafari && (
          <li className="flex items-start gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-500 text-sm font-bold text-white">
              1
            </span>
            <div>
              <p className="font-medium text-gray-900">
                하단의{" "}
                <span className="inline-flex items-center rounded bg-gray-200 px-1.5 py-0.5 font-mono text-xs">
                  ···
                </span>{" "}
                (더 보기) 버튼 탭
              </p>
            </div>
          </li>
        )}

        <li className="flex items-start gap-3">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-500 text-sm font-bold text-white">
            {isNewSafari ? "2" : "1"}
          </span>
          <div>
            <p className="font-medium text-gray-900">
              {isNewSafari ? "" : "하단의 "}
              <span className="inline-flex items-center gap-1">
                <ShareIcon className="h-4 w-4" />
                공유
              </span>{" "}
              버튼 탭
            </p>
          </div>
        </li>

        <li className="flex items-start gap-3">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-500 text-sm font-bold text-white">
            {isNewSafari ? "3" : "2"}
          </span>
          <div>
            <p className="font-medium text-gray-900">
              <span className="inline-flex items-center gap-1">
                <PlusSquareIcon className="h-4 w-4" />홈 화면에 추가
              </span>{" "}
              선택
            </p>
          </div>
        </li>

        <li className="flex items-start gap-3">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-500 text-sm font-bold text-white">
            {isNewSafari ? "4" : "3"}
          </span>
          <div>
            <p className="font-medium text-gray-900">
              우측 상단의{" "}
              <span className="font-semibold text-blue-500">추가</span> 탭
            </p>
          </div>
        </li>
      </ol>
    </div>
  );
}

/**
 * Android 설치 버튼 (beforeinstallprompt 사용 가능 시)
 */
function AndroidInstallButton({ onInstall }: { onInstall: () => void }) {
  return (
    <button
      onClick={onInstall}
      className="w-full max-w-sm rounded-xl bg-blue-500 px-6 py-4 text-lg font-semibold text-white shadow-lg transition-colors hover:bg-blue-600 active:bg-blue-700"
    >
      앱 설치하기
    </button>
  );
}

/**
 * Android 설치 안내 (beforeinstallprompt 불가 시)
 */
function AndroidInstallGuide() {
  return (
    <div className="w-full max-w-sm rounded-2xl bg-gray-50 p-6">
      <p className="mb-4 text-center text-sm font-medium text-gray-700">
        Chrome에서 아래 단계를 따라 설치해주세요
      </p>

      <ol className="space-y-4">
        <li className="flex items-start gap-3">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-500 text-sm font-bold text-white">
            1
          </span>
          <div>
            <p className="font-medium text-gray-900">
              우측 상단의{" "}
              <span className="inline-flex items-center rounded bg-gray-200 px-1.5 py-0.5 font-mono text-xs">
                ⋮
              </span>{" "}
              메뉴 버튼 탭
            </p>
          </div>
        </li>

        <li className="flex items-start gap-3">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-500 text-sm font-bold text-white">
            2
          </span>
          <div>
            <p className="font-medium text-gray-900">
              &quot;앱 설치&quot; 또는 &quot;홈 화면에 추가&quot; 선택
            </p>
          </div>
        </li>

        <li className="flex items-start gap-3">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-500 text-sm font-bold text-white">
            3
          </span>
          <div>
            <p className="font-medium text-gray-900">
              <span className="font-semibold text-blue-500">설치</span> 버튼 탭
            </p>
          </div>
        </li>
      </ol>
    </div>
  );
}

/**
 * iOS 공유 아이콘
 */
function ShareIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
      />
    </svg>
  );
}

/**
 * 홈 화면에 추가 아이콘
 */
function PlusSquareIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 4v16m8-8H4"
      />
    </svg>
  );
}
